import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api from '@/api';
import {
  ScanLine, FileText, X, ArrowRight, DollarSign,
  AlertTriangle, CheckCircle2, Cpu
} from 'lucide-react';
import CostEstimate from '@/components/CostEstimate';
import ProcessingProgress from '@/components/ProcessingProgress';

const scanTips = [
  '300 DPI minimum — 600 DPI recommended for faded drawings',
  'Flatten creased pages before scanning for best deskew results',
  'Include scale bars and title blocks for auto-calibration',
  'TIFF or PDF preferred for large format scans',
];

export default function DigitizeUploadPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState('upload'); // upload | estimate | processing
  const [jobId, setJobId] = useState(null);
  const [costEstimate, setCostEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/tiff': ['.tiff', '.tif'],
    },
    multiple: true,
  });

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (files.length === 0 || !projectName.trim()) return;
    setUploading(true);
    setStep('estimate');
    try {
      const jobRes = await api.post('/drawing-jobs', {
        project_name: projectName,
        notes,
        mode: 'digitize',
      });
      const id = jobRes.data.id;
      setJobId(id);

      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      await api.post(`/drawing-jobs/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setEstimating(true);
      try {
        const estRes = await api.post(`/drawing-jobs/${id}/estimate-cost`, null, { timeout: 12000 });
        setCostEstimate(estRes.data);
      } catch {
        setCostEstimate({
          total_pages: files.length,
          pages_needing_vision: files.length,
          pages_skipped: 0,
          total_estimated_cost: (files.length * 0.04).toFixed(2),
          estimated_time_minutes: (files.length * 0.3).toFixed(1),
          cost_breakdown: { vision_analysis: (files.length * 0.04).toFixed(4), classification: 0 },
          per_page: [],
        });
      } finally {
        setEstimating(false);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setStep('upload');
    } finally {
      setUploading(false);
    }
  };

  const confirmProcessing = async () => {
    if (!jobId) return;
    setStep('processing');
    try {
      await api.post(`/drawing-jobs/${jobId}/process`);
    } catch (err) {
      console.error('Processing start failed:', err);
    }
  };

  const handleProcessingComplete = () => {
    setTimeout(() => navigate(`/jobs/${jobId}`), 1500);
  };

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-3xl" data-testid="digitize-upload-page">
      <div className="flex items-center gap-3 mb-1">
        <ScanLine className="w-5 h-5 text-primary" />
        <h1 className="font-heading font-bold text-2xl tracking-tight uppercase text-foreground">Digitize Drawings</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-8">Upload scanned paper drawings for AI-powered digitization</p>

      {step === 'processing' ? (
        <div className="space-y-4 animate-fade-in" data-testid="digitize-processing-view">
          <ProcessingProgress jobId={jobId} onComplete={handleProcessingComplete} />
          <p className="text-xs text-muted-foreground font-mono text-center">
            You can navigate away — processing continues in the background.
          </p>
        </div>
      ) : step === 'estimate' ? (
        <div className="space-y-4 animate-fade-in" data-testid="digitize-estimate-view">
          {estimating ? (
            <div className="flex flex-col items-center py-12">
              <DollarSign className="w-8 h-8 text-amber-400 animate-pulse" />
              <p className="font-heading font-bold text-sm uppercase tracking-wider text-foreground mt-4">Analyzing Scanned Drawings</p>
              <p className="text-xs text-muted-foreground mt-1">Classifying pages and estimating cost...</p>
            </div>
          ) : costEstimate ? (
            <CostEstimate estimate={costEstimate} onConfirm={confirmProcessing} confirming={false} />
          ) : null}
        </div>
      ) : (
        <div className="space-y-6 animate-fade-in">
          {/* Scan Tips */}
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-800/30 rounded-sm p-4" data-testid="scan-tips">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span className="font-heading font-bold text-xs uppercase tracking-wider text-amber-600 dark:text-amber-400">Scan Quality Tips</span>
            </div>
            <ul className="space-y-1.5">
              {scanTips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-200/70">
                  <CheckCircle2 className="w-3 h-3 text-amber-600 dark:text-amber-500 mt-0.5 shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Drop Zone */}
          <div
            {...getRootProps()}
            data-testid="digitize-dropzone"
            className={`border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-blue-50 dark:bg-blue-950/10'
                : 'border-border hover:border-muted-foreground bg-card'
            }`}
            style={{
              backgroundImage: !isDragActive ? 'url(https://images.unsplash.com/photo-1613969673101-c982c30287f2?crop=entropy&cs=srgb&fm=jpg&q=85)' : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundBlendMode: 'overlay',
            }}
          >
            <input {...getInputProps()} />
            <ScanLine className={`w-10 h-10 mx-auto mb-3 ${isDragActive ? 'text-primary' : 'text-muted-foreground'}`} />
            <p className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">
              {isDragActive ? 'Drop scanned drawings here' : 'Drag & drop scanned drawings'}
            </p>
            <p className="text-xs text-muted-foreground mt-1 font-mono">PDF, PNG, JPG, TIFF — scanned blueprints, floor plans, hand-drawn sketches</p>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="bg-card border border-border rounded-sm divide-y divide-border">
              {files.map((file, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5" data-testid={`digitize-file-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="font-mono text-xs text-foreground/80 truncate">{file.name}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button onClick={() => removeFile(i)} className="p-1 hover:bg-secondary rounded text-muted-foreground hover:text-rose-400 transition-colors" data-testid={`remove-file-${i}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <div className="px-4 py-2 bg-secondary/20">
                <span className="text-[10px] font-mono text-muted-foreground">{files.length} drawing(s) ready for digitization</span>
              </div>
            </div>
          )}

          {/* Project Name */}
          {files.length > 0 && (
            <div className="bg-card border border-border rounded-sm p-5 space-y-4">
              <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground mb-3">Project Details</h3>
              <div>
                <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Building / Project Name *</label>
                <input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  data-testid="digitize-project-name"
                  className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-2.5 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                  placeholder="e.g. Main Street Office Building — 1995 Plans"
                />
              </div>
              <div>
                <label className="block font-heading font-medium text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Notes (optional)</label>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="digitize-notes"
                  className="w-full bg-card border border-border focus:border-primary focus:ring-1 focus:ring-orange-500/50 rounded-sm px-3 py-2.5 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                  placeholder="e.g. Floors 1-3 only, hand-drawn annotations on floor 2"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={uploading || !projectName.trim()}
                data-testid="digitize-submit-btn"
                className="flex items-center gap-2 border-l-2 border-primary bg-primary/10 hover:bg-primary/15 text-primary font-heading font-bold uppercase tracking-wider text-sm h-11 px-6 transition-colors disabled:opacity-50 mt-4"
              >
                <Cpu className="w-4 h-4" />
                {uploading ? 'Uploading...' : 'Start Digitization'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
